import { useMemo, useEffect, useCallback, useRef } from "react";
import type { KeyboardEvent } from "react";
import * as acp from "@agentclientprotocol/sdk";
import {
  createBrowserAgent,
  type BrowserClient,
} from "@pineseed/acp/sdk/browser-agent";
import { type ConnectionConfig } from "@pineseed/gui/hook/useConnectionStatus";

import { useParticipantId } from "@pineseed/gui/hook/useParticipantId";
import {
  useComposerApp,
  type UseComposerAppReturn,
} from "@pineseed/interface-gui/hook/useComposerApp";
import { useMessageParticipants } from "@pineseed/workspace/hooks/useMessageParticipants";
import { useTags } from "@pineseed/workspace/hooks/useTags";
import { ParticipantId } from "@pineseed/gui/participant";
import { Participant, Tag } from "@pineseed/gui/types";

export interface AcpCustomToolDefinition {
  name: string;
  description: string;
  tags: string[];
  category: string;
  version: string;
  timeoutSeconds: number;
  exclusive: boolean;
  estimatedCostTokens: number;
  requiresCredentials: boolean;
  provider: string;
  credentialKey: string;
  embedding: number[];
  executionMode: number;
  // Plain JSON schema — edge converts to Struct via Struct.fromJson
  inputSchema: Record<string, unknown>;
}

export interface AcpCustomTool {
  definition: AcpCustomToolDefinition;
  handle: (
    args: Record<string, unknown>,
  ) => Promise<Record<string, unknown>> | Record<string, unknown>;
}

// ACP permits extension keys on ClientCapabilities; PineSeed advertises browser tools via `_customTools`.
type AcpClientCapabilities = acp.ClientCapabilities & {
  _customTools: AcpCustomToolDefinition[];
};

export interface UseAcpChatProps {
  httpUrl: string;
  wsUrl: string;
  acpWsUrl: string;
  workspaceKey: string;
  isAuthenticated: boolean;
  location: string;
  acpLoadSessionTimeoutMs: number;
  acpUploadTtlS: number;
  customTools: AcpCustomTool[];
  onAcpSessionUpdate: (sessionId: string, update: acp.SessionUpdate) => void;
  onSubmitted: (content: string) => Promise<void>;
  onPromptLifecycle: () => void;
}

export interface UseAcpChatReturn {
  // Participant
  participantId: ParticipantId | null;

  // Connection
  connectionConfig: ConnectionConfig | undefined;

  // AuthZ
  isAuthenticated: boolean;

  // Composer state
  textInputHeight: UseComposerAppReturn["textInputHeight"];
  setTextInputHeight: UseComposerAppReturn["setTextInputHeight"];
  sendOnEnter: UseComposerAppReturn["sendOnEnter"];
  setSendOnEnter: UseComposerAppReturn["setSendOnEnter"];
  showGlobalLoading: UseComposerAppReturn["showGlobalLoading"];
  typingUsers: UseComposerAppReturn["typingUsers"];

  // Composition hooks
  hasText: UseComposerAppReturn["hasText"];
  setGetTextFn: UseComposerAppReturn["setGetTextFn"];
  setGetContentWithTagsFn: UseComposerAppReturn["setGetContentWithTagsFn"];
  setClearTextFn: UseComposerAppReturn["setClearTextFn"];
  onTextChange: UseComposerAppReturn["onTextChange"];
  handleSubmit: () => void;
  onEditorReady: UseComposerAppReturn["onEditorReady"];
  onKeyDown: (e: KeyboardEvent) => void;
  inputActionHandlers: UseComposerAppReturn["inputActionHandlers"];

  tags: Tag[];
  parseTextWithTags: (text: string) => {
    type: "text" | "valid-tag" | "invalid-tag" | "escaped-at";
    content: string;
    tag?: Tag;
  }[];
  extractTagNameForDisplay: (content: string) => string;
  getMessageDisplayInfo: (
    participantId: string,
    messageRole: "user" | "assistant" | "system",
  ) => {
    name: string;
    avatar: string | undefined;
    fallback: string;
    isBot: boolean;
    role: string | null;
    status: "online" | "offline" | "away" | "busy" | "typing";
  };
  getParticipantInfo: (participantId: string) => Participant | null;

  wsUrl: string;
  httpUrl: string;

  // ACP session ID — available once session is established
  acpSessionId: string | null;
}

export function useAcpChat({
  httpUrl,
  wsUrl,
  acpWsUrl,
  workspaceKey,
  isAuthenticated,
  location,
  acpLoadSessionTimeoutMs,
  acpUploadTtlS,
  customTools,
  onAcpSessionUpdate,
  onSubmitted,
  onPromptLifecycle,
}: UseAcpChatProps): UseAcpChatReturn {
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable refs so the ACP effect never re-runs due to callback/array identity changes
  const onAcpSessionUpdateRef = useRef(onAcpSessionUpdate);
  onAcpSessionUpdateRef.current = onAcpSessionUpdate;
  const customToolsRef = useRef(customTools);
  customToolsRef.current = customTools;

  // ACP agent refs — stable across renders, no state churn
  const agentRef = useRef<acp.Agent | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const disconnectRef = useRef<(() => Promise<void>) | null>(null);

  // Captures the editor's clearText fn when the editor becomes ready
  const clearTextRef = useRef<(() => void) | null>(null);

  const { participantId } = useParticipantId();

  const { getMessageDisplayInfo, getParticipantInfo } =
    useMessageParticipants();

  const { tags, parseTextWithTags, extractTagNameForDisplay } = useTags();

  // Connection monitoring (health source — tenant-scoped)
  const wsUrlWithParams = useMemo(() => {
    if (!participantId?.id) return null;
    const base = wsUrl.replace(/\/ws$/, "/system/ws");
    const url = new URL(base);
    url.searchParams.set("participantId", participantId.id);
    return url.toString();
  }, [wsUrl, participantId?.id]);

  const connectionConfig = useMemo<ConnectionConfig | undefined>(() => {
    if (!wsUrlWithParams || !isAuthenticated) return undefined;
    return {
      websocket: {
        url: wsUrlWithParams,
        reconnectInterval: 3000,
        maxReconnectInterval: 60000,
        maxReconnectAttempts: 8,
      },
      http: {
        url: `${httpUrl}/health`,
        interval: 30000,
        timeout: 5000,
      },
    };
  }, [wsUrlWithParams, httpUrl, isAuthenticated]);

  // ACP WebSocket connection — one session per page load
  useEffect(() => {
    if (!participantId?.id || !isAuthenticated || !workspaceKey) return;

    const url = new URL(acpWsUrl);
    url.searchParams.set("participantId", participantId.id);
    if (workspaceKey) url.searchParams.set("workspaceKey", workspaceKey);
    const ws = new WebSocket(url.toString());

    ws.onopen = async () => {
      console.log("🟢 [ACP] WebSocket open");

      const client: BrowserClient = {
        async sessionUpdate({ sessionId, update }) {
          console.log(
            "🟢 [ACP] sessionUpdate",
            sessionId,
            update.sessionUpdate,
          );
          onAcpSessionUpdateRef.current(sessionId, update);
        },
        async requestPermission(_params) {
          // Hook point: show permission dialog when implemented
          return {
            outcome: { outcome: "cancelled" },
          } satisfies acp.RequestPermissionResponse;
        },
        async handleCustomTool(toolName, args) {
          const tool = customToolsRef.current.find(
            (t) => t.definition.name === toolName,
          );
          if (tool) {
            // Errors propagate naturally — PineSeedAgent returns isError=true ToolResult.
            return tool.handle(args);
          }
          throw new Error(`[BrowserAgent] unknown custom tool: ${toolName}`);
        },
      };

      try {
        const { agent, disconnect } = await createBrowserAgent({
          apiUrl: httpUrl,
          ws,
          participantId: participantId.id,
          location,
          loadSessionTimeoutMs: acpLoadSessionTimeoutMs,
          appUrl: window.location.origin,
          uploadTtlS: acpUploadTtlS,
          client,
          extraHeaders: { "X-Workspace-Key": workspaceKey },
        });

        const clientCapabilities: AcpClientCapabilities = {
          _customTools: customToolsRef.current.map((t) => t.definition),
        };

        await agent.initialize({
          protocolVersion: acp.PROTOCOL_VERSION,
          clientCapabilities,
        });

        const { sessionId } = await agent.newSession({
          cwd: window.location.href,
          mcpServers: [],
        });

        agentRef.current = agent;
        sessionIdRef.current = sessionId;
        disconnectRef.current = disconnect;

        console.log("🟢 [ACP] Session established", sessionId);
      } catch (err) {
        console.error("🔴 [ACP] Failed to establish session", err);
        ws.close();
      }
    };

    ws.onerror = (err) => {
      console.error("🔴 [ACP] WebSocket error", err);
    };

    ws.onclose = () => {
      console.log("🔴 [ACP] WebSocket closed");
      agentRef.current = null;
      sessionIdRef.current = null;
      disconnectRef.current = null;
    };

    return () => {
      disconnectRef
        .current?.()
        .catch((err: unknown) => console.error("🔴 [ACP] Disconnect error", err));
    };
  }, [
    participantId?.id,
    acpWsUrl,
    workspaceKey,
    httpUrl,
    isAuthenticated,
    location,
    acpLoadSessionTimeoutMs,
    acpUploadTtlS,
  ]);

  const {
    textInputHeight,
    setTextInputHeight,
    sendOnEnter,
    setSendOnEnter,
    showGlobalLoading,
    typingUsers,
    hasText,
    setGetTextFn,
    setGetContentWithTagsFn,
    setClearTextFn: _setClearTextFn,
    onTextChange,
    onEditorReady: composerOnEditorReady,
    getContentWithTagsFn,
    emitComposingEvent,
    inputActionHandlers,
  } = useComposerApp({
    workspaceId: "",
    conversationId: "",
    threadId: "",
    wsUrl: wsUrlWithParams,
    httpUrl,
    isAuthenticated,
    participantId,
    // Submission is owned by useAcpChat via ACP — this is intentionally a no-op
    onSubmitted: async (_content: string) => {},
  });

  // Intercept onEditorReady to capture clearText before forwarding
  const onEditorReady = useCallback(
    (
      getText: () => string,
      getContentWithTags: () => string,
      clearText: () => void,
      initialHasText: boolean,
      setText: (text: string) => void,
    ) => {
      clearTextRef.current = clearText;
      composerOnEditorReady(
        getText,
        getContentWithTags,
        clearText,
        initialHasText,
        setText,
      );
    },
    [composerOnEditorReady],
  );

  // ACP-backed handleSubmit — replaces useComposerApp's handleSubmit entirely
  const handleSubmit = useCallback(() => {
    if (!agentRef.current || !sessionIdRef.current) {
      console.warn("🟡 [ACP] handleSubmit called before session ready");
      return;
    }

    const content = getContentWithTagsFn();
    if (!content.trim()) return;

    const agent = agentRef.current;
    const sessionId = sessionIdRef.current;

    // Clear editor and close bubble immediately (fire and forget)
    clearTextRef.current?.();
    onSubmitted(content).catch((err: unknown) =>
      console.error("🔴 [ACP] onSubmitted callback error", err),
    );

    onPromptLifecycle();

    // Fire and forget — hook point: attach cancellation when implemented
    Promise.resolve(
      agent.prompt({
        sessionId,
        prompt: [{ type: "text", text: content }],
      }),
    )
      .then((response: acp.PromptResponse) => {
        console.log("🟢 [ACP] prompt stop", response.stopReason);
        onPromptLifecycle();
      })
      .catch((err: unknown) => {
        console.error("🔴 [ACP] prompt error", err);
      });
  }, [getContentWithTagsFn, onSubmitted, onPromptLifecycle]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (hasText) {
        if (e.key === "Enter") {
          console.log("⮑ Key pressed");
          e.preventDefault();
          handleSubmit();
        } else {
          if (throttleTimerRef.current) {
            return;
          } else {
            emitComposingEvent(getContentWithTagsFn());
            throttleTimerRef.current = setTimeout(() => {
              throttleTimerRef.current = null;
            }, 1000);
          }
        }
      }
      return () => {
        if (throttleTimerRef.current) {
          clearTimeout(throttleTimerRef.current);
          throttleTimerRef.current = null;
        }
      };
    },
    [handleSubmit, hasText, getContentWithTagsFn, emitComposingEvent],
  );

  return {
    participantId,

    connectionConfig,

    isAuthenticated,

    textInputHeight,
    setTextInputHeight,
    sendOnEnter,
    setSendOnEnter,
    showGlobalLoading,
    typingUsers,

    hasText,
    setGetTextFn,
    setGetContentWithTagsFn,
    setClearTextFn: _setClearTextFn as UseAcpChatReturn["setClearTextFn"],
    onTextChange,
    handleSubmit,
    onEditorReady,
    onKeyDown,
    inputActionHandlers,

    tags,
    parseTextWithTags,
    extractTagNameForDisplay,
    getMessageDisplayInfo,
    getParticipantInfo,

    wsUrl: wsUrlWithParams ?? "",
    httpUrl,

    acpSessionId: sessionIdRef.current,
  };
}
